// loading.ts
import { CSSProperties } from "react";
import { getSubAppPortalContainer } from "@eweadn-qiankun/shared-utils";

// 定义 Loading 配置项类型
interface LoadingOptions {
  /** 加载文案，默认为"加载中..." */
  text?: string;
  /** 加载图标颜色，默认为 #1677ff */
  color?: string;
  /** 加载图标大小，单位 px，默认为 24 */
  size?: number;
  /** 是否显示背景遮罩，默认为 true */
  showMask?: boolean;
  /** 自定义容器样式 */
  style?: CSSProperties;
}

// 单例标识：避免重复创建 Loading DOM
let loadingInstance: HTMLElement | null = null;

/**
 * 全局 Loading 控制函数
 * @param visible 是否显示 Loading（true 显示，false 关闭）
 * @param options 可选配置项（仅显示时生效）
 */
export const useLoading = (visible: boolean, options: LoadingOptions = {}) => {
  // 解构配置项，设置默认值
  const {
    text = "加载中...",
    color = "#333333",
    size = 24,
    showMask = true,
    style = {},
  } = options;

  // 1. 显示 Loading
  if (visible) {
    // 单例校验：如果已存在 Loading 实例，直接返回（避免重复创建）
    if (loadingInstance) return;

    // 创建 Loading 容器
    const container = document.createElement("div");
    container.id = "global-loading";
    loadingInstance = container;

    // 设置容器样式（核心布局 + 自定义样式）
    Object.assign(container.style, {
      position: "fixed",
      top: "0",
      left: "0",
      right: "0",
      bottom: "0",
      zIndex: "9999",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      ...(showMask ? { backgroundColor: "rgba(255, 255, 255, 0.8)" } : {}),
      ...style,
    } as CSSProperties);

    // 创建加载动画图标
    const spinner = document.createElement("div");
    Object.assign(spinner.style, {
      width: `${size}px`,
      height: `${size}px`,
      border: `3px solid ${color}33`, // 透明边框（33 是透明度）
      borderTopColor: color,
      borderRadius: "50%",
      animation: "loading-spin 1s linear infinite",
      marginBottom: "12px",
    } as CSSProperties);
    container.appendChild(spinner);

    // 创建加载文案
    const textNode = document.createElement("span");
    textNode.innerText = text;
    Object.assign(textNode.style, {
      color,
      fontSize: "14px",
    } as CSSProperties);
    container.appendChild(textNode);

    // 创建动画样式（全局生效，避免重复创建）
    if (!document.getElementById("loading-animation-style")) {
      const styleNode = document.createElement("style");
      styleNode.id = "loading-animation-style";
      styleNode.innerHTML = `
        @keyframes loading-spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `;
      document.head.appendChild(styleNode);
    }

    const portalParent = getSubAppPortalContainer();
    portalParent.appendChild(container);
  } else {
    // 2. 关闭 Loading：移除 DOM 并清空实例
    if (loadingInstance?.parentNode) {
      loadingInstance.parentNode.removeChild(loadingInstance);
      loadingInstance = null;
    }
  }
};
